// src/components/CalendarView.js
import React, { useState } from 'react';

import Icon from "../../components/em/Icon";
import PropTypes from 'prop-types';
import { localDateStr } from '../../utils/localDate';
import { formatDuration, formatHoursDuration } from '../../utils/duration';







// Present is green and leave is orange. Those two were the other way round —
// an orange bar on a day worked and a green one on a day off — which read
// backwards against every other status indicator in the product.
const STATUS_COLORS = {
  present: '#4caf50',        // green — overridden per-day by ARRIVAL_COLORS below
  'in-progress': '#1e90ff',  // blue
  leave: '#ff7a00',          // orange
  absent: '#f44336',         // red
  holiday: '#9c27b0',        // purple
};

// A present/in-progress day is further coloured by how the punch-in compared
// to the scheduled start — same three buckets, same thresholds, and the same
// colours as the Company Attendance "today" view (CompanyAttendance.js's
// SECTIONS), so this monthly grid can't tell a different story about the
// same arrival. `notMarked` never reaches a coloured bar (it renders no bar
// at all) so it has no colour here.
const ARRIVAL_COLORS = {
  earlyBird: '#155dfc', // T.info600
  onTime: '#4caf50',    // same green as `present` — the common case
  late: '#b45309',      // matches CompanyAttendance's "Late" amber
};

const ARRIVAL_LABELS = {
  earlyBird: 'Early Bird',
  onTime: 'On Time',
  late: 'Late Arrival',
};

// Week off has no entry here on purpose: it is the one key with no colour bar,
// so its grey lives in CSS (`.cal-off` / `.legend-glyph` in em-attendance.css)
// as the --em-fg7 / --em-bg-muted token pair. Tokens rather than a fixed hex
// so the "O" stays legible in the dark theme too.

const STATUS_LABELS = {
  present: 'On Time',
  'in-progress': 'In Progress',
  leave: 'On Leave',
  absent: 'Absent — no attendance marked',
  holiday: 'Holiday',
  weekoff: 'Week off — not a working day on this shift',
};

// A rostered day off. Derived per employee from their shift's working_days
// (falling back to the company weekly-offs), so two people on different shifts
// see different cells greyed. Rendered as a centred "O" instead of a status
// bar — there is no attendance to report on a day nobody was expected in.
const OFF_DAY = 'weekoff';

// The key below the grid, driven off STATUS_COLORS so a colour can never be
// changed for the markers and missed for the legend (or the reverse). Week off
// carries the same "O" glyph the cells do rather than a colour swatch, because
// that is what the reader is matching against.
const LEGEND = [
  { key: 'present', label: 'On Time', color: ARRIVAL_COLORS.onTime },
  { key: 'earlyBird', label: 'Early Bird', color: ARRIVAL_COLORS.earlyBird },
  { key: 'late', label: 'Late Arrival', color: ARRIVAL_COLORS.late },
  { key: 'in-progress', label: 'In Progress' },
  { key: 'leave', label: 'On Leave' },
  { key: 'absent', label: 'Absent' },
  { key: 'holiday', label: 'Holiday' },
  { key: OFF_DAY, label: 'Week Off', glyph: 'O' },
];

const CalendarView = ({ data = [], month: monthProp, onMonthChange }) => {
  /* ---------------------------
     STATE: MONTH & YEAR

     Controlled when the parent passes `month` ('YYYY-MM'). It has to be: the
     grid only ever holds the month the parent FETCHED, and this component used
     to page through months in local state without telling anyone — so every
     month but the current one rendered as an empty grid, because no request was
     ever made for it. Uncontrolled use still works for any caller that only
     needs the current month.
  --------------------------- */
  const today = new Date();
  const [ownYear, setOwnYear] = useState(today.getFullYear());
  const [ownMonth, setOwnMonth] = useState(today.getMonth());

  const controlled = typeof monthProp === 'string' && /^\d{4}-\d{2}$/.test(monthProp);
  const year = controlled ? Number(monthProp.slice(0, 4)) : ownYear;
  const month = controlled ? Number(monthProp.slice(5, 7)) - 1 : ownMonth;

  // Step by `delta` months, normalising the year rollover in one place rather
  // than in each arrow handler.
  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    if (controlled) {
      onMonthChange?.(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      return;
    }
    setOwnYear(d.getFullYear());
    setOwnMonth(d.getMonth());
  };

  /* ---------------------------
     MAP DATA BY DATE
  --------------------------- */
  const dataMap = {};
  data.forEach(d => {
    dataMap[d.date] = d;
  });

  /* ---------------------------
     MONTH CALCULATION
  --------------------------- */
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startWeekday = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(year, month, d));
  }

  const isToday = (d) =>
    d.toDateString() === new Date().toDateString();

  /* ---------------------------
     MONTH NAVIGATION
  --------------------------- */
  const goPrev = () => shiftMonth(-1);
  const goNext = () => shiftMonth(1);

  /* ---------------------------
     RENDER
  --------------------------- */
  return (
    <div className="month-cal">


<div className="title-btns">
  <div className="icn-subtit">
    <span className="icon-wrap"><Icon name="clock" color="#f54b01" /></span>
    <h3>Monthly Calendar</h3>
  </div>
  <div className="btn-wrap"></div>
</div>


      {/* HEADER */}
      <div className="calendar-header">
        <button onClick={goPrev} className="nav-btn">◀</button>
        <h4 className="calendar-title">
          {new Date(year, month).toLocaleString('en-US', {
            month: 'long',
            year: 'numeric'
          })}
        </h4>
        <button onClick={goNext} className="nav-btn">▶</button>
      </div>

      {/* CALENDAR GRID */}
      <div className="main-calendar">

        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(h => (
          <div key={h} className="cal-title">{h}</div>
        ))}

        {cells.map((dateObj, idx) => {
          if (!dateObj) {
            return <div key={idx} className="cal-date empty"></div>;
          }

          // MUST be the local day: `cells` holds `new Date(year, month, d)` =
          // LOCAL midnight, so toISOString() here returned the previous UTC day
          // in any zone ahead of UTC (IST) and painted every record one cell to
          // the right — attendance marked on the 13th showed up on the 14th.
          const key = localDateStr(dateObj);
          const record = dataMap[key];
          const status = record?.status;
          const hours = record?.working_hours;
          // Exact seconds when the API supplies them — working_hours is stored
          // to 2dp (36s granularity), so it cannot print an accurate duration.
          const workedSeconds = record?.worked_seconds;
          const isOffDay = status === OFF_DAY;

          return (
            <div
              key={idx}
              className={`cal-date cal-fill ${isToday(dateObj) ? 'today' : ''}${isOffDay ? ' off-day' : ''}`}
              // The grid is read-only, so there is nothing to disable — this
              // just tells assistive tech the cell is inactive, matching the
              // greyed-out styling sighted users get.
              aria-disabled={isOffDay || undefined}
              title={isOffDay ? STATUS_LABELS[OFF_DAY] : undefined}
            >
              <div className="cal-inner">
                <div className="cal-day">{dateObj.getDate()}</div>

                {isOffDay ? (
                  // role="img" makes the aria-label authoritative — a bare
                  // aria-label on a plain div is not reliably announced, and
                  // "O" read out as a letter says nothing.
                  <div className="cal-off" role="img" aria-label="Off day">O</div>
                ) : status ? (
                  <div
                    className="attd-mark"
                    // Names the marker on hover — a bare coloured bar makes the
                    // reader match it against the legend. `label` carries the
                    // holiday's name when the API knows it; arrival (Early
                    // Bird / On Time / Late Arrival) is more specific than the
                    // bare "On Time" status label, so it wins when present.
                    title={
                      record?.label ||
                      (record?.arrival && ARRIVAL_LABELS[record.arrival]) ||
                      STATUS_LABELS[status] ||
                      status
                    }
                    style={{
                      background:
                        (record?.arrival && ARRIVAL_COLORS[record.arrival]) ||
                        STATUS_COLORS[status] ||
                        '#e0e0e0'
                    }}
                  />
                ) : null}

                {(workedSeconds != null || hours != null) && (
                  <div className="cal-hours">
                    {workedSeconds != null
                      ? formatDuration(workedSeconds)
                      : formatHoursDuration(hours)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* LEGEND — from LEGEND/STATUS_COLORS, never hard-coded here again. */}
      <div className="calendar-mark">
        {LEGEND.map(({ key, label, glyph, color }) => (
          <Legend key={key} color={color || STATUS_COLORS[key]} glyph={glyph} label={label} />
        ))}
      </div>
    </div>
  );
};

CalendarView.propTypes = {
  data: PropTypes.array,
  /** 'YYYY-MM' — pass it to control which month the grid shows. */
  month: PropTypes.string,
  /** Called with the next 'YYYY-MM' when an arrow is clicked (controlled only). */
  onMonthChange: PropTypes.func,
};

const Legend = ({ color, glyph, label }) => (
  <div className="legend-item">
    {glyph ? (
      <span className="legend-glyph" aria-hidden="true">{glyph}</span>
    ) : (
      <span className="legend-dot" style={{ background: color }} />
    )}
    <p>{label}</p>
  </div>
);

export default CalendarView;
